import { Coordinates } from '@/models/Coordinates'
import { RouteDirectionsResponse } from '@/models/Route'

/**
 * Transforms a RouteDirectionsResponse from our backend into a Google Maps DirectionsResult
 * that's compatible with the existing useGoogleDirections composable.
 *
 * This creates a minimal DirectionsResult with all required fields populated,
 * focusing on the data actually used by the composable (overview_path and bounds).
 *
 * @param routeResponse - The response from our backend route API
 * @param originalRequest - The original request parameters
 * @param googleMaps - The Google Maps API instance (from gmapApi())
 */
export function transformRouteResponseToDirectionsResult(
  routeResponse: RouteDirectionsResponse,
  originalRequest: {
    origin: Coordinates
    destination: Coordinates
    waypoints?: Coordinates[]
  },
  googleMaps: any // The Google Maps API instance
): google.maps.DirectionsResult {
  // Convert our Coordinates to Google LatLng objects
  const overviewPath: google.maps.LatLng[] = routeResponse.overviewPath.map(
    (coord) => new googleMaps.maps.LatLng(coord.lat, coord.lng)
  )

  // Calculate bounds from overview path
  const bounds = new googleMaps.maps.LatLngBounds()
  overviewPath.forEach((point) => bounds.extend(point))

  // Convert legs on the routeResponse to a format compatible with Google Maps
  // Transform legs
  const legs: google.maps.DirectionsLeg[] = routeResponse.legs.map((leg) => {
    // Transform steps
    const steps: google.maps.DirectionsStep[] = leg.steps.map((step) => ({
      distance: step.distance
        ? {
            text: metersToText(step.distance),
            value: step.distance,
          }
        : undefined,
      duration: step.staticDuration
        ? {
            text: secondsToText(step.staticDuration),
            value: step.staticDuration,
          }
        : undefined,
      encoded_lat_lngs: step.encodedPolyline || '',
      end_location: createLatLng(step.endLocation, googleMaps),
      end_point: createLatLng(step.endLocation, googleMaps),
      instructions: step.navigationInstructions || '',
      lat_lngs: [], // Deprecated, but required
      maneuver: '',
      path: [], // Would need to decode polyline if needed
      start_location: createLatLng(step.startLocation, googleMaps),
      start_point: createLatLng(step.startLocation, googleMaps),
      travel_mode: googleMaps.maps.TravelMode.DRIVING,
    }))

    return {
      distance: leg.distance
        ? {
            text: metersToText(leg.distance),
            value: leg.distance,
          }
        : undefined,
      duration: leg.staticDuration
        ? {
            text: secondsToText(leg.staticDuration),
            value: leg.staticDuration,
          }
        : undefined,
      end_address: '',
      end_location: createLatLng(leg.endLocation, googleMaps),
      start_address: '',
      start_location: createLatLng(leg.startLocation, googleMaps),
      steps,
      traffic_speed_entry: [],
      via_waypoints: [],
    }
  })

  const route: google.maps.DirectionsRoute = {
    bounds,
    copyrights: '',
    legs,
    overview_path: overviewPath,
    overview_polyline: '',
    summary: '',
    warnings: [],
    waypoint_order: [],
  }

  const request: google.maps.DirectionsRequest = {
    origin: originalRequest.origin,
    destination: originalRequest.destination,
    waypoints:
      originalRequest.waypoints?.map((wp) => ({
        location: wp,
        stopover: true,
      })) || [],
    travelMode: googleMaps.maps.TravelMode.DRIVING,
  }

  return {
    request,
    routes: [route],
    geocoded_waypoints: [],
  }
}

function createLatLng(
  location: { lat: number; lng: number } | null,
  googleMaps: any
): google.maps.LatLng {
  if (!location || location.lat == null || location.lng == null) {
    return new googleMaps.maps.LatLng(0, 0) // Return a default LatLng if location is invalid
  }
  return new googleMaps.maps.LatLng(location.lat, location.lng)
}

function metersToText(meters: number): string {
  if (meters < 1000) {
    return `${meters} m`
  } else {
    return `${(meters / 1000).toFixed(1)} km`
  }
}

function secondsToText(seconds: number): string {
  const time = Math.round(seconds / 60)
  return `${time} min`
}
